// The two clocks an offer runs on.
//
// The OFFER WINDOW (startsAt / endsAt) decides when an offer is on the shelf.
// A CODE'S LIFE (expiresAtHours / expiresAtDateTime) decides how long what a
// fan took off that shelf keeps working. They are independent: a giveaway can
// be claimable all season with each code good for a day, or claimable for one
// hour with codes good until the end of the month.
//
// Part one: when an offer is live, beyond the Visibility switch.
//
// Visibility is a manual on/off an operator flips. A schedule is the same
// answer decided in advance: a happy hour, a giveaway that opens when gates
// open and closes at the final whistle. Both have to agree, and both are
// enforced in three places that cannot import each other — this file, the
// fan app's ProfileScreen, and functions/lib/offerCodes.js — so the rule is
// written once here and mirrored deliberately in the other two.
//
// Blank means unbounded in that direction. An offer with neither runs until
// somebody hides it, which is exactly how every offer behaved before this
// existed, so nothing already saved changes meaning.

export const OFFER_OPEN = 'open';
export const OFFER_SCHEDULED = 'scheduled';   // its start has not arrived
export const OFFER_ENDED = 'ended';           // its end has passed

function boundary(value) {
    const ms = Number(value);
    // 0 and NaN both mean "not set". A real boundary is always a timestamp.
    return Number.isFinite(ms) && ms > 0 ? ms : null;
}

/**
 * Where this offer sits relative to its own schedule, ignoring Visibility.
 * Kept separate from `active` so the admin can say WHY an offer is not
 * showing — "starts Friday" is a different problem from "somebody hid it".
 */
export function offerWindowState(offer, now = Date.now()) {
    const startsAt = boundary(offer && offer.startsAt);
    const endsAt = boundary(offer && offer.endsAt);
    if (startsAt && now < startsAt) return OFFER_SCHEDULED;
    // The end is exclusive: an offer that ends at 9pm is gone AT 9pm, which is
    // what "until 9" means to the person who typed it.
    if (endsAt && now >= endsAt) return OFFER_ENDED;
    return OFFER_OPEN;
}

/** Is the schedule letting this offer through right now? */
export function offerIsOpen(offer, now = Date.now()) {
    return offerWindowState(offer, now) === OFFER_OPEN;
}

/** Everything that has to be true for a fan to see it. */
export function offerIsLive(offer, now = Date.now()) {
    return !!offer && offer.active !== false && offerIsOpen(offer, now);
}

// ---- the admin form talks in local wall-clock time ----

/** A datetime-local input value to epoch ms, or null when it is blank. */
export function dateTimeInputToEpoch(value) {
    if (!value) return null;
    const ms = new Date(value).getTime();
    return Number.isFinite(ms) ? ms : null;
}

/**
 * Epoch ms back to what a datetime-local input wants, in the operator's own
 * timezone — toISOString would shift the time they typed.
 */
export function epochToDateTimeInput(ms) {
    const value = boundary(ms);
    if (!value) return '';
    const d = new Date(value);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
        + `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * A moment as the person reading it would say it.
 *
 * The year shows only when it is not the current one. Leaving it off always is
 * the bug this fixes: a use-by in a later year printed as "Jul 1", which reads
 * as a date that has already gone by. Printing it always is its own small lie,
 * because nobody needs the year on a code that dies tomorrow and it crowds a
 * line that is often the last thing a fan reads before the counter.
 *
 * The time stays in either case. A code that dies at 1:20 AM has not lasted
 * the day, and rounding it to a date would promise most of one.
 */
export function formatMoment(ms, now = Date.now()) {
    const d = new Date(ms);
    const thisYear = d.getFullYear() === new Date(now).getFullYear();
    return d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        ...(thisYear ? {} : { year: 'numeric' }),
        hour: 'numeric',
        minute: '2-digit'
    });
}

/** A short human phrase for the list and the save summary, or '' if unbounded. */
export function describeWindow(offer, now = Date.now()) {
    const startsAt = boundary(offer && offer.startsAt);
    const endsAt = boundary(offer && offer.endsAt);
    const when = (ms) => formatMoment(ms, now);
    if (startsAt && endsAt) return `${when(startsAt)} until ${when(endsAt)}`;
    if (startsAt) return `from ${when(startsAt)}`;
    if (endsAt) return `until ${when(endsAt)}`;
    return '';
}

// ---------------------------------------------------------------------------
// Part two: how long a code works once a fan has it.
//
// Field names match the MLB rewards in v1 (expiresAtHours / expiresAtDateTime)
// so the vocabulary is the same across Sqwad, and the "whichever comes sooner"
// rule is v1's too. Three of v1's habits are deliberately NOT carried over,
// because each of them lies to somebody:
//
//   - Blank meant a hidden one-month default there. Here blank means never.
//   - A date already in the past silently became one month. Here it stays in
//     the past and the admin is told what that means.
//   - Nothing on the redemption path ever checked either field; the expiry was
//     real only because MLB honoured it. Here the server enforces it.

/**
 * When THIS fan's code stops working, resolved at the moment they claim it.
 *
 * Frozen onto the assignment rather than recomputed later: a fan promised
 * twenty four hours keeps twenty four hours even if the offer is edited
 * afterwards, and the app and the server never have to agree about "now".
 *
 * Returns null for "never expires".
 */
export function resolveCodeExpiry(offer, claimedAtMs) {
    const hours = Number(offer && offer.expiresAtHours);
    const fromHours = Number.isFinite(hours) && hours > 0
        ? claimedAtMs + hours * 3600 * 1000
        : null;
    const absolute = boundary(offer && offer.expiresAtDateTime);
    if (fromHours && absolute) return Math.min(fromHours, absolute);
    return fromHours || absolute || null;
}

/** Has a code's frozen expiry passed? A missing expiry never has. */
export function codeIsExpired(expiresAt, now = Date.now()) {
    const at = boundary(expiresAt);
    return !!at && now >= at;
}

/** How the offer's code life reads on a form, or '' when codes never expire. */
export function describeCodeExpiry(offer, now = Date.now()) {
    const hours = Number(offer && offer.expiresAtHours);
    const hasHours = Number.isFinite(hours) && hours > 0;
    const absolute = boundary(offer && offer.expiresAtDateTime);
    const by = absolute && formatMoment(absolute, now);
    if (hasHours && absolute) {
        return `${hours} hour${hours === 1 ? '' : 's'} after a fan gets it, or ${by} — whichever comes first`;
    }
    if (hasHours) return `${hours} hour${hours === 1 ? '' : 's'} after a fan gets it`;
    if (absolute) return `until ${by}`;
    return '';
}
